<script setup>
/**
 * One condition an audience is narrowed by.
 *
 * THE CONDITION LIST IS PER FIELD AND COMES FROM THE SERVER. It is not a UI nicety: the
 * BFF derives it from the tables in `broadcast-filter.ts`, which are the only thing
 * standing between an owner and a query Postgres cannot run — Chatwoot validates operators
 * for its standard columns only, and for a detail the account defined itself there is no
 * upstream check at all. So this component never composes a list of its own; it renders
 * the one it was given, and changing the field resets the condition rather than carrying a
 * condition the new field may not accept.
 *
 * THE VALUE EDITOR FOLLOWS THE CONDITION, NOT THE FIELD. "has any value" needs no box at
 * all, and "is more than N days ago" wants a whole number of days rather than a date — the
 * server says which of the six kinds it is, and this switches on that. A single text box
 * for all of them is how "2026-02-30" reaches a strict ISO parser that raises.
 */
import { computed } from 'vue';

import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** `{ id, field, operator, value }` from `helpers/broadcast-compose.js`. */
  row: { type: Object, required: true },
  /** Every field, each with its own allowed conditions. */
  fields: { type: Array, default: () => [] },
  canRemove: { type: Boolean, default: true },
});

const emit = defineEmits(['update', 'remove']);

const { t } = useWasloI18n();

const fieldOptions = computed(() =>
  props.fields.map(field => ({ value: field.key, label: field.label }))
);

const chosenField = computed(
  () => props.fields.find(field => field.key === props.row.field) ?? null
);

const operatorOptions = computed(() =>
  (chosenField.value?.operators ?? []).map(operator => ({
    value: operator.id,
    label: operator.label,
  }))
);

const valueKind = computed(() => {
  const operator = (chosenField.value?.operators ?? []).find(
    candidate => candidate.id === props.row.operator
  );
  return operator?.valueKind ?? 'none';
});

const booleanOptions = computed(() => [
  { value: 'true', label: t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.YES') },
  { value: 'false', label: t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.NO') },
]);

const valueHint = computed(() => {
  if (valueKind.value === 'days') {
    return t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.DAYS_HINT');
  }
  if (valueKind.value === 'date') {
    return t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.DATE_HINT');
  }
  return '';
});

// Changing the field drops the condition AND the value. A condition the new field does not
// accept would be refused at count time, a minute later, with a sentence about a field the
// owner has already stopped thinking about.
const onField = field =>
  emit('update', { ...props.row, field, operator: '', value: '' });
// Changing the condition keeps the value only when the editor is the same kind of thing.
const onOperator = operator => {
  const before = valueKind.value;
  const after =
    (chosenField.value?.operators ?? []).find(
      candidate => candidate.id === operator
    )?.valueKind ?? 'none';
  emit('update', {
    ...props.row,
    operator,
    value: before === after ? props.row.value : '',
  });
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1 sm:flex-row sm:items-end"
    data-waslo-audience-row
  >
    <div class="flex-1 min-w-0">
      <WasloSelect
        :model-value="row.field"
        :options="fieldOptions"
        :label="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.FIELD')"
        :placeholder="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.FIELD_PLACEHOLDER')"
        data-waslo-audience-field
        @update:model-value="onField"
      />
    </div>

    <div class="flex-1 min-w-0">
      <WasloSelect
        :model-value="row.operator"
        :options="operatorOptions"
        :label="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.CONDITION')"
        :placeholder="
          t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.CONDITION_PLACEHOLDER')
        "
        :disabled="!chosenField"
        data-waslo-audience-operator
        @update:model-value="onOperator"
      />
    </div>

    <!-- No box at all when the condition takes no value — a disabled one still reads as
         something you forgot to fill in. -->
    <div v-if="valueKind !== 'none'" class="flex-1 min-w-0">
      <WasloSelect
        v-if="valueKind === 'boolean'"
        :model-value="row.value"
        :options="booleanOptions"
        :label="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.VALUE')"
        :placeholder="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.VALUE')"
        data-waslo-audience-value
        @update:model-value="value => emit('update', { ...row, value })"
      />
      <WasloInput
        v-else
        :model-value="row.value"
        :label="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.VALUE')"
        :message="valueHint"
        data-waslo-audience-value
        @update:model-value="value => emit('update', { ...row, value })"
      />
    </div>

    <button
      v-if="canRemove"
      type="button"
      class="inline-flex items-center justify-center h-10 gap-1 px-3 text-sm rounded-lg shrink-0 text-n-slate-11 bg-n-alpha-2 hover:bg-n-slate-4"
      :aria-label="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.REMOVE')"
      data-waslo-audience-remove
      @click="emit('remove')"
    >
      <span class="size-4 i-lucide-trash-2" aria-hidden="true" />
    </button>
  </div>
</template>
